/**
 * Importar — os três caminhos de trazer dado em massa para o projeto.
 *
 * **Pinout**: colar da planilha ou mandar o arquivo (o que segue abaixo).
 * **Campos em lote**: os quatro Excel do menu antigo, que alteram apelido,
 * descrição e localização do componente e o pino do pinout — com conferência
 * antes de gravar, que o sistema antigo não tem.
 * **Apelidos sugeridos**: o nome que outro projeto já deu ao mesmo componente.
 *
 * ---
 *
 * Importar pinout — colar da planilha ou mandar o arquivo.
 *
 * É o "Incluir por arquivo do Excel" do Sistema de hoje, mais o caminho de
 * colar, que é como a maior parte do trabalho realmente chega: a pessoa tem a
 * planilha aberta, seleciona um pedaço e cola.
 *
 * **A tela é um caminho de três passos, não um formulário.** Traz o dado, diz o
 * que é cada coluna, confere e importa. O passo do meio é o que existe porque
 * planilha nenhuma vem no formato do sistema — e errar a coluna aqui é gravar
 * cor no lugar do circuito em duzentas linhas.
 *
 * **Confere antes de gravar.** A pré-visualização mostra as primeiras linhas já
 * traduzidas para os campos do pinout, com as que serão descartadas marcadas.
 * A importação passa pelo mesmo `Verifica`/dedup do lançamento (RN-02 a RN-05):
 * o que não existe é criado, duplicata é ignorada, e o projeto fica marcado
 * para reprocessar.
 *
 * **Colar JSON** é o "Incluir Json" do sistema antigo (28/09/2026): o mesmo
 * texto que a equipe já colava lá, lido na tela (`jsonDoPinout`) — sem passo
 * de colunas, porque o JSON já diz o que é cada campo. A grade do pinout abre
 * direto nele pelo botão "Incluir JSON" (`?fonte=json`).
 */

import { useDeferredValue, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  CAMPOS_IMPORTACAO, importarExcel, importarJson, MAPA_VAZIO, montarLinhas, processarTexto,
  type CampoDeImportacao, type Mapa,
} from "@/api/importacao"
import { chaves } from "@/api/query"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { IncluirPinoutsResponse } from "@/api/tipos"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { ApelidosSugeridos, LoteDeComponentes } from "./LoteDeComponentes"
import { lerJsonDePinout } from "./jsonDoPinout"

type Fonte = "colar" | "planilha" | "json"

const EXEMPLO_JSON = '{"pinouts":[{"component_codigo":"B001","component_descricao":"BATERIA","conector":"AA",'
  + '"terminal_pino":"1","circuito":"B001_1.A","cor":"RD","obs":"KL30"}]}'

type Modo = "pinout" | "lote" | "apelidos"

/**
 * A aba Importar tem três caminhos, e os três são "trazer dado em massa para
 * este projeto": o pinout (colar ou planilha), os quatro Excel em lote que
 * alteram campo de componente, e as sugestões de apelido — que costumam ser
 * melhores que a planilha, porque o nome já existe em outro projeto.
 */
export function Importacao() {
  const [parametros] = useSearchParams()
  // ?modo=lote abre direto em Campos em lote (o botão "Apelidos por planilha" do pinout).
  const [modo, setModo] = useState<Modo>(parametros.get("modo") === "lote" ? "lote" : "pinout")
  const fonteInicial: Fonte = parametros.get("fonte") === "json" ? "json" : "colar"

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{
        display: "flex", gap: 4, padding: "10px 18px 0",
        borderBottom: "1px solid var(--line2)",
      }}>
        {([
          ["pinout", "Pinout"],
          ["lote", "Campos em lote"],
          ["apelidos", "Apelidos sugeridos"],
        ] as [Modo, string][]).map(([chave, rotulo]) => (
          <button
            key={chave}
            onClick={() => setModo(chave)}
            style={{
              border: 0, borderBottom: "2px solid " + (modo === chave ? "var(--acao)" : "transparent"),
              background: "transparent", cursor: "pointer", padding: "6px 12px",
              fontSize: 12.5, color: modo === chave ? "var(--txt)" : "var(--txt2)",
              fontWeight: modo === chave ? 600 : 400,
            }}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {modo === "pinout" ? <ImportarPinout fonteInicial={fonteInicial} />
        : modo === "lote" ? <LoteDeComponentes />
        : <ApelidosSugeridos />}
    </div>
  )
}

function ImportarPinout({ fonteInicial }: { fonteInicial: Fonte }) {
  const { projetoId } = useParams()
  const id = Number(projetoId)
  const qc = useQueryClient()

  const [fonte, setFonte] = useState<Fonte>(fonteInicial)
  const [texto, setTexto] = useState("")
  const [json, setJson] = useState("")
  // Ler o JSON é local e instantâneo; o adiamento só poupa a digitação de reler um texto grande a cada tecla.
  const jsonAdiado = useDeferredValue(json)
  const lido = useMemo(
    () => (jsonAdiado.trim() === "" ? null : lerJsonDePinout(jsonAdiado)),
    [jsonAdiado],
  )
  const [celulas, setCelulas] = useState<string[][]>([])
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [origem, setOrigem] = useState("")
  const [mapa, setMapa] = useState<Mapa>(MAPA_VAZIO)
  const [resultado, setResultado] = useState<IncluirPinoutsResponse | null>(null)
  const campoArquivo = useRef<HTMLInputElement>(null)

  const analisar = useMutation({
    mutationFn: () => processarTexto(texto),
    onSuccess: (r) => {
      setCelulas(r.celulas)
      setResultado(null)
      // Chute inicial pela ordem mais comum de planilha de pinout. É chute, e a
      // tela deixa claro que é: a conferência abaixo mostra o resultado.
      setMapa({
        ...MAPA_VAZIO,
        componentCodigo: 0,
        conector: r.celulas[0] && r.celulas[0].length > 1 ? 1 : -1,
        terminalPino: r.celulas[0] && r.celulas[0].length > 2 ? 2 : -1,
        circuito: r.celulas[0] && r.celulas[0].length > 3 ? 3 : -1,
        cor: r.celulas[0] && r.celulas[0].length > 4 ? 4 : -1,
      })
    },
  })

  // As linhas coladas que viram pinout, e de que linha veio cada uma.
  const colado = useMemo(() => montarLinhas(celulas, mapa), [celulas, mapa])

  const importar = useMutation({
    mutationFn: async () => {
      if (fonte === "planilha") {
        if (!arquivo) throw new Error("Escolha a planilha.")
        return importarExcel(id, origem.trim(), mapa, arquivo)
      }
      if (fonte === "json") {
        if (!lido?.ok) throw new Error("Cole um JSON válido.")
        return importarJson(id, origem.trim(), lido.itens)
      }
      return importarJson(id, origem.trim(), colado.itens)
    },
    onSuccess: (r) => {
      setResultado(r)
      qc.invalidateQueries({ queryKey: ["pinouts", id] })
      qc.invalidateQueries({ queryKey: ["catalogo"] })
      qc.invalidateQueries({ queryKey: chaves.projeto(id) })
    },
  })

  const colunas = useMemo(
    () => Math.max(0, ...celulas.map((l) => l.length)),
    [celulas],
  )

  const itens = useMemo(
    () => (fonte === "colar" ? colado.itens
      : fonte === "json" && lido?.ok ? lido.itens : []),
    [fonte, colado, lido],
  )
  const descartadas = celulas.length - itens.length
  const lidas = fonte === "json" ? (lido?.ok ? lido.total : 0) : celulas.length

  const faltamObrigatorios = fonte === "json" ? [] : CAMPOS_IMPORTACAO
    .filter((c) => c.obrigatorio && mapa[c.chave as CampoDeImportacao] < 0)
    .map((c) => c.rotulo)

  const podeImportar = fonte === "planilha"
    ? arquivo != null && faltamObrigatorios.length === 0
    : itens.length > 0 && faltamObrigatorios.length === 0

  // A API numera as mensagens pela lista que recebeu, sem as linhas descartadas;
  // quem colou conta pelas linhas (ou itens) dele.
  const ondeNaOrigem = (indice: number) => fonte === "json" && lido?.ok
    ? `item ${(lido.posicoes[indice] ?? indice) + 1}`
    : fonte === "colar"
      ? `linha ${(colado.posicoes[indice] ?? indice) + 1}`
      : `linha ${indice + 1}`

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 18 }}>
        <div style={{ maxWidth: 900, display: "flex", flexDirection: "column", gap: 20 }}>

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Passo n={1} titulo="De onde vem" />

            <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 8,
                          background: "var(--surface3)", alignSelf: "flex-start" }}>
              {(["colar", "planilha", "json"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => { setFonte(f); setResultado(null) }}
                  aria-pressed={fonte === f}
                  style={{
                    height: 28, padding: "0 13px", borderRadius: 6, fontSize: 12.5,
                    fontWeight: fonte === f ? 600 : 500,
                    background: fonte === f ? "var(--surface)" : "transparent",
                    color: fonte === f ? "var(--txt)" : "var(--txt2)",
                    boxShadow: fonte === f ? "var(--sombra)" : undefined,
                  }}
                >
                  {f === "colar" ? "Colar da planilha" : f === "planilha" ? "Mandar o arquivo" : "Colar JSON"}
                </button>
              ))}
            </div>

            {fonte === "json" ? (
              <>
                <textarea
                  value={json}
                  onChange={(e) => { setJson(e.target.value); setResultado(null) }}
                  rows={10}
                  placeholder={EXEMPLO_JSON}
                  aria-label="JSON dos pinouts"
                  spellCheck={false}
                  style={{
                    width: "100%", padding: "9px 11px", borderRadius: 8, resize: "vertical",
                    border: `1px solid ${lido && !lido.ok ? "var(--erro)" : "var(--line2)"}`,
                    background: "var(--surface2)", fontSize: 12, fontFamily: "var(--fm)", lineHeight: 1.5,
                  }}
                />
                <span style={{ fontSize: 11.5, color: "var(--txt3)", lineHeight: 1.5 }}>
                  O mesmo JSON do "Incluir Json" do sistema antigo: <code>{"{\"pinouts\": [ … ]}"}</code> ou só
                  a lista <code>[ … ]</code>. Campos: <code>component_codigo</code>, <code>component_descricao</code>,{" "}
                  <code>conector</code>, <code>terminal_pino</code>, <code>circuito</code> (os cinco obrigatórios),{" "}
                  <code>cor</code>, <code>chicote</code>, <code>restricao</code>, <code>obs</code>.
                </span>
                {lido && !lido.ok && (
                  <div role="alert" style={{
                    padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
                    background: "var(--erro-soft)", color: "var(--erro-on-soft)", wordBreak: "break-word",
                  }}>
                    {lido.erro}
                  </div>
                )}
              </>
            ) : fonte === "colar" ? (
              <>
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={7}
                  placeholder={"Cole aqui. Uma linha por pinout, colunas separadas por tabulação —\n" +
                               "é o que sai ao copiar direto da planilha."}
                  aria-label="Texto colado da planilha"
                  style={{
                    width: "100%", padding: "9px 11px", borderRadius: 8, resize: "vertical",
                    border: "1px solid var(--line2)", background: "var(--surface2)",
                    fontSize: 12, fontFamily: "var(--fm)", lineHeight: 1.5,
                  }}
                />
                <button
                  onClick={() => analisar.mutate()}
                  disabled={texto.trim() === "" || analisar.isPending}
                  style={{
                    alignSelf: "flex-start", height: 32, padding: "0 13px", borderRadius: 7,
                    background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
                    opacity: texto.trim() === "" ? 0.45 : 1,
                  }}
                >
                  {analisar.isPending ? "Lendo…" : "Ler o que foi colado"}
                </button>
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <input
                  ref={campoArquivo}
                  type="file"
                  accept=".xlsx"
                  aria-label="Planilha"
                  onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setResultado(null) }}
                  style={{ fontSize: 12.5 }}
                />
                <span style={{ fontSize: 11.5, color: "var(--txt3)", maxWidth: "48ch", lineHeight: 1.5 }}>
                  Só <code>.xlsx</code>. A planilha inteira vai para o servidor, que lê as colunas
                  pelos números do passo 2 — aqui não há pré-visualização.
                </span>
              </div>
            )}

            <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 340 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
                Fonte (de onde este dado veio)
              </span>
              <input
                value={origem}
                onChange={(e) => setOrigem(e.target.value)}
                placeholder="manual de serviço, planilha do cliente…"
                style={{
                  width: "100%", height: 32, padding: "0 9px", borderRadius: 7,
                  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
                }}
              />
              <span style={{ fontSize: 11, color: "var(--txt3)" }}>
                Vai em todas as linhas importadas. É o que permite achar de novo o lote depois.
              </span>
            </label>
          </section>

          {fonte !== "json" && (celulas.length > 0 || fonte === "planilha") && (
            <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Passo n={2} titulo="O que é cada coluna" />
              <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)", lineHeight: 1.5 }}>
                {fonte === "colar"
                  ? `Foram lidas ${celulas.length} linhas e ${colunas} colunas. Diga qual coluna é qual campo — o chute abaixo é só um começo.`
                  : "Diga em que coluna da planilha está cada campo. A contagem começa em 1, como na planilha."}
              </p>

              <div className="grade-formulario" style={{
                display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10,
              }}>
                {CAMPOS_IMPORTACAO.map((c) => (
                  <label key={c.chave} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
                      {c.rotulo}
                      {c.obrigatorio && <span style={{ color: "var(--erro)", marginLeft: 3 }}>*</span>}
                    </span>
                    <select
                      value={mapa[c.chave as CampoDeImportacao]}
                      onChange={(e) => setMapa({ ...mapa, [c.chave]: Number(e.target.value) })}
                      style={{
                        width: "100%", height: 32, padding: "0 9px", borderRadius: 7,
                        border: `1px solid ${c.obrigatorio && mapa[c.chave as CampoDeImportacao] < 0
                          ? "var(--erro)" : "var(--line2)"}`,
                        background: "var(--surface2)", fontSize: 12.5,
                      }}
                    >
                      <option value={-1}>— não usar —</option>
                      {Array.from({ length: fonte === "colar" ? colunas : 20 }, (_, i) => (
                        <option key={i} value={i}>
                          coluna {i + 1}
                          {fonte === "colar" && celulas[0]?.[i] ? ` · ${celulas[0][i]!.slice(0, 18)}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>

              {faltamObrigatorios.length > 0 && (
                <span style={{ fontSize: 11.5, color: "var(--erro)", lineHeight: 1.5 }}>
                  Falta dizer a coluna de: {faltamObrigatorios.join(", ")}.
                  <br />
                  <span style={{ color: "var(--txt3)" }}>
                    A API recusa a linha que não tenha os cinco — sem eles a importação não grava nada.
                  </span>
                </span>
              )}
            </section>
          )}

          {fonte === "json" && lido?.ok && (
            <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Passo n={2} titulo="Confira antes de gravar" />
              <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)" }}>
                {lido.total} {lido.total === 1 ? "item lido" : "itens lidos"}. <b>{itens.length}</b>{" "}
                {itens.length === 1 ? "será importado" : "serão importados"}
                {lido.recusadas.length > 0 && (
                  <span style={{ color: "var(--acao-on-soft)" }}>
                    {" "}e <b>{lido.recusadas.length}</b> {lido.recusadas.length === 1 ? "fica" : "ficam"} de fora
                  </span>
                )}.
              </p>
              {lido.recusadas.length > 0 && (
                <ul aria-label="Itens que ficam de fora" style={{
                  margin: 0, padding: "9px 11px 9px 27px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.6,
                  background: "var(--acao-soft)", color: "var(--acao-on-soft)",
                }}>
                  {lido.recusadas.slice(0, 10).map((r) => (
                    <li key={r.item}>item {r.item}: {r.motivo}</li>
                  ))}
                  {lido.recusadas.length > 10 && <li>… e mais {lido.recusadas.length - 10}.</li>}
                </ul>
              )}
              {lido.desconhecidos.length > 0 && (
                <span style={{ fontSize: 11.5, color: "var(--acao-on-soft)", lineHeight: 1.5 }}>
                  Campos que não são do pinout e não serão gravados:{" "}
                  {lido.desconhecidos.slice(0, 10).map((c, i) => (
                    <span key={c}>{i > 0 && ", "}<code>{c}</code></span>
                  ))}
                  {lido.desconhecidos.length > 10 && ` e mais ${lido.desconhecidos.length - 10}`}.
                </span>
              )}
            </section>
          )}

          {((fonte === "colar" && celulas.length > 0 && faltamObrigatorios.length === 0)
            || (fonte === "json" && itens.length > 0)) && (
            <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {fonte === "colar" && (
                <>
                  <Passo n={3} titulo="Confira antes de gravar" />
                  <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)" }}>
                    As primeiras linhas já traduzidas. <b>{itens.length}</b> serão importadas
                    {descartadas > 0 && (
                      <span style={{ color: "var(--acao-on-soft)" }}>
                        {" "}e <b>{descartadas}</b> descartadas por faltar um dos cinco campos exigidos
                      </span>
                    )}.
                  </p>
                </>
              )}

              <div style={{ overflowX: "auto", border: "1px solid var(--line2)", borderRadius: 10 }}>
                <table style={{ fontSize: 11.5, minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: "var(--surface3)" }}>
                      {CAMPOS_IMPORTACAO.map((c) => (
                        <th key={c.chave} style={{ padding: "7px 9px", textAlign: "left",
                                                   fontSize: 10.5, color: "var(--txt2)", whiteSpace: "nowrap" }}>
                          {c.rotulo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {itens.slice(0, 8).map((x, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                        {CAMPOS_IMPORTACAO.map((c) => (
                          <td key={c.chave} className="mono"
                              style={{ padding: "6px 9px", whiteSpace: "nowrap",
                                       color: x[c.chave] ? "var(--txt)" : "var(--txt3)" }}>
                            {x[c.chave] || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {itens.length > 8 && (
                <span style={{ fontSize: 11, color: "var(--txt3)" }}>
                  …e mais {itens.length - 8} linhas.
                </span>
              )}
            </section>
          )}

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={() => importar.mutate()}
              disabled={!podeImportar || importar.isPending}
              style={{
                alignSelf: "flex-start", height: 36, padding: "0 16px", borderRadius: 8,
                background: podeImportar ? "var(--brand)" : "var(--surface)",
                color: podeImportar ? "#FFFFFF" : "var(--txt3)",
                border: podeImportar ? undefined : "1px solid var(--line2)",
                fontSize: 13, fontWeight: 600, opacity: importar.isPending ? 0.6 : 1,
              }}
            >
              {importar.isPending
                ? "Importando…"
                : fonte === "planilha"
                  ? "Importar a planilha"
                  : fonte === "json"
                    ? `Importar ${itens.length} ${itens.length === 1 ? "pinout" : "pinouts"}`
                    : `Importar ${itens.length} ${itens.length === 1 ? "linha" : "linhas"}`}
            </button>

            {resultado && (
              <div style={{
                padding: "11px 13px", borderRadius: 9, fontSize: 12, lineHeight: 1.6,
                background: "var(--ok-soft)", color: "var(--ok-on-soft)",
              }}>
                <b>{resultado.criados} {resultado.criados === 1 ? "pinout criado" : "pinouts criados"}.</b>
                {resultado.ignorados > 0 && (
                  <> {resultado.ignorados} {resultado.ignorados === 1 ? "linha ignorada" : "linhas ignoradas"}
                    {" "}— duplicadas ou incompletas.</>
                )}
                {(resultado.mensagens?.length ?? 0) > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                    {resultado.mensagens!.slice(0, 10).map((m, i) => (
                      <li key={i} style={{ fontSize: 11 }}>
                        {ondeNaOrigem(m.indice)}{m.campo ? ` · ${m.campo}` : ""}: {m.texto}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {(importar.error ?? analisar.error) != null && (
              <Erro erro={importar.error ?? analisar.error} />
            )}
          </section>
        </div>
      </div>

      <BarraDeEstado>
        {lidas > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{lidas}</b> lidas
            {" · "}
            <b className="num" style={{ color: "var(--txt)" }}>{itens.length}</b> prontas
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>
          A importação cria o que não existe e ignora duplicata, como o lançamento normal.
        </span>
      </BarraDeEstado>
    </div>
  )
}

function Passo({ n, titulo }: { n: number; titulo: string }) {
  return (
    <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600 }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 20, height: 20, borderRadius: "50%", background: "var(--brand)",
        color: "#FFFFFF", fontSize: 11, fontWeight: 700,
      }}>
        {n}
      </span>
      {titulo}
    </h2>
  )
}

function Erro({ erro }: { erro: unknown }) {
  const trava = erro instanceof ErroDeTrava
  return (
    <div style={{
      padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
      background: trava ? "var(--acao-soft)" : "var(--erro-soft)",
      color: trava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
      border: trava ? "1px solid var(--acao)" : undefined,
    }}>
      {trava && <b style={{ display: "block", marginBottom: 3 }}>Bloqueado pela trava do canário</b>}
      {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível importar."}
    </div>
  )
}
